import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { cancelBooking } from '@hqo/service-booking-client';

import { useApi } from 'app/shared/contexts/api';

export const CANCEL_EVENT_BOOKING_MUTATION_KEY = 'activity-cancel-event-booking';

export interface CancelEventBookingVariables {
  /** The event the booking belongs to — service-booking scopes cancellation under it. */
  eventUuid: string;
  /** The booking being cancelled. In the feed this is the activity item's own `uuid`. */
  bookingUuid: string;
}

/**
 * Cancel an event booking, through service-booking's own client rather than a hand-built url — SBS
 * owns and versions that route.
 *
 * `retry: false`: the failures worth surfacing are deterministic — past the notice deadline, already
 * cancelled, or the event was cancelled — and a retry re-earns the same 400.
 *
 * Refetching the feed is the caller's job, not this hook's. The projection is a Kafka hop plus an
 * OpenSearch refresh behind, so invalidating on success would return the pre-cancel state; the feed
 * refetches when the user dismisses the modal instead.
 */
export const useCancelEventBookingMutation = (): UseMutationResult<void, Error, CancelEventBookingVariables> => {
  const { apiUrl, getAuthTokenValue } = useApi();

  return useMutation<void, Error, CancelEventBookingVariables>({
    mutationKey: [CANCEL_EVENT_BOOKING_MUTATION_KEY],
    mutationFn: async ({ eventUuid, bookingUuid }: CancelEventBookingVariables) => {
      const result = await cancelBooking(
        { baseUrl: apiUrl, eventUuid, bookingUuid },
        { authToken: await getAuthTokenValue() },
      );

      if (!result.isSuccess()) {
        throw result.errorData;
      }
    },
    retry: false,
  });
};
